import React, { useEffect, useState, useMemo, useCallback } from 'react';
import {
    Card,
    CircularLoader,
    Divider,
    AlertBar,
    SingleSelect,
    SingleSelectOption, 
} from '@dhis2/ui';
import { Calendar1Icon, ExpandIcon, ViewIcon, UserIcon } from 'lucide-react';
import { useDataEngine, useDataQuery } from '@dhis2/app-runtime';
import {
    PieChart, Pie, Cell, Tooltip, ResponsiveContainer,
    LineChart, Line, XAxis, YAxis, CartesianGrid,
    BarChart, Bar, RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
} from 'recharts';
import { useIndicators, useOrgUnit, useDataElements } from './hooks/useData';
import Objectives from './components/Objectives'
import RealTasks from './components/RealTasks'
import RealGoals from './components/RealGoals'
import TaskMetrics from './components/TaskMetrics'
import LeadershipDashboard from './components/LeadershipDashboard'
import ReviewReminders from './components/ReviewReminders'
import KPIKRAReports from './components/KPIKRAReports';
import { usePerformanceHierarchy } from './hooks/usePerformanceHierarchy'; // Add this import
import {
    getFiscalYearLabel, resolveActiveFiscalYear, isWithinFiscalYear, getValidatedFiscalYear
} from '../GoalsManagement/utils/fiscalYearHelpers';

    
    // Helper to check if a date falls within a fiscal year
    const isDateInFiscalYear = (dateString, fiscalYearStart, fiscalYearEnd) => {
        if (!dateString || !fiscalYearStart || !fiscalYearEnd) return false;
        
        const date = new Date(dateString);
        const start = new Date(fiscalYearStart);
        const end = new Date(fiscalYearEnd);
        
        // Set end date to end of day for inclusive comparison
        end.setHours(23, 59, 59, 999);
        
        return date >= start && date <= end;
    };

    // Helper to get fiscal year from a date
    const getFiscalYearFromDate = (dateString, fiscalYearPattern) => {
        if (!dateString || !fiscalYearPattern?.start) return null;
        
        const date = new Date(dateString);
        const fiscalStart = new Date(fiscalYearPattern.start);
        
        // Get the year based on when the fiscal year starts
        let fiscalYear = date.getFullYear();
        
        // If current date is before fiscal year start, it belongs to previous fiscal year
        if (date < fiscalStart && fiscalStart.getMonth() > 0) {
            fiscalYear = date.getFullYear() - 1;
        }
        
        return fiscalYear;
        };

        const H4 = ({ children, style }) => (
            <div style={{ fontSize: 24, fontWeight: '700', marginBottom: 24, color: '#212529', ...style }}>
                {children}
            </div>
        );
        const H5 = ({ children, style }) => (
            <div style={{ fontSize: 20, fontWeight: '600', marginBottom: 16, color: '#343a40', ...style }}>
                {children}
            </div>
        );
        const BodyText = ({ children, style }) => (
            <div style={{ fontSize: 14, color: '#555', lineHeight: 1.5, ...style }}>
                {children}
            </div>
        );

    const MetricCard = ({ title, value, subtitle, color = '#007bff' }) => (
        <Card style={{ padding: 16, textAlign: 'center', height: '100%' }}>
            <div style={{ fontSize: 32, fontWeight: '700', color: color, marginBottom: 8 }}>
                {value}
            </div>
            <div style={{ fontSize: 16, fontWeight: '600', color: '#343a40', marginBottom: 4 }}>
                {title}
            </div>
            {subtitle && (
                <div style={{ fontSize: 12, color: '#6c757d' }}>
                    {subtitle}
                </div>
            )}
        </Card>
    );

    const statusColors = {
        'On Track': '#28a745',
        'At Risk': '#ffc107',
        'Behind Schedule': '#dc3545',
    };

    const DATA_ELEMENT_IDS = {
        goalStart: 'MH7DQVegD1j',
        goalEnd: 'pUvJQ9vy3A3',
        objectiveStart: 'eDcVy661j9e',
        objectiveEnd: 'J0rjxrDKn1l',
        taskWeek: 'EiL3lPaL908',
    };

    const parseLocalDate = (value) => {
        if (!value) return null;
        const normalized = String(value).split('T')[0];
        const [year, month, day] = normalized.split('-').map(Number);
    if (!year || !month || !day) return null;
    const parsed = new Date(year, month - 1, day);
    if (
        parsed.getFullYear() !== year ||
        parsed.getMonth() !== month - 1 ||
        parsed.getDate() !== day
    ) {
        return null;
    }
    return parsed;
};

const getDataValue = (dataValues = [], dataElement) =>
    dataValues.find(dv => dv.dataElement === dataElement)?.value || '';

const shiftFiscalYearRange = (range, yearOffset) => {
    if (!range?.start || !range?.end) return range;
    const start = parseLocalDate(range.start);
    const end = parseLocalDate(range.end);
    if (!start || !end) return range;

    return {
        start: `${start.getFullYear() + yearOffset}-${String(start.getMonth() + 1).padStart(2, '0')}-${String(start.getDate()).padStart(2, '0')}`,
        end: `${end.getFullYear() + yearOffset}-${String(end.getMonth() + 1).padStart(2, '0')}-${String(end.getDate()).padStart(2, '0')}`,
    };
};

const getFallbackFiscalYear = () => {
    const now = new Date();
    const currentYear = now.getFullYear();
    
    // April 1 of current year to March 31 of next year
    const start = `${currentYear}-04-01`;
    const end = `${currentYear + 1}-03-31`;
    
    console.log('📅 Using fallback fiscal year (April-March):', { start, end });
    return { start, end };
};

// Simplified buildFiscalYearOptions - generates correct fiscal year options
const buildFiscalYearOptions = (storedCalendar) => {
    // Get the fiscal year pattern from stored calendar
    let patternStartMonth = 3; // April (0-indexed: 0=Jan, 3=Apr)
    let patternStartDay = 1;
    let patternEndMonth = 2; // March (0-indexed: 2=Mar)
    let patternEndDay = 31;
    
    if (storedCalendar?.financialYearStart && storedCalendar?.financialYearEnd) {
        const startDate = new Date(storedCalendar.financialYearStart);
        const endDate = new Date(storedCalendar.financialYearEnd);
        patternStartMonth = startDate.getMonth();
        patternStartDay = startDate.getDate();
        patternEndMonth = endDate.getMonth();
        patternEndDay = endDate.getDate();
        console.log('📅 Using stored fiscal year pattern:', {
            start: `${patternStartMonth + 1}-${patternStartDay}`,
            end: `${patternEndMonth + 1}-${patternEndDay}`
        });
    }
    
    // Calculate current fiscal year based on TODAY'S DATE
    const now = new Date();
    const currentYear = now.getFullYear();
    
    // Create fiscal year start date using the pattern
    let fiscalStartDate = new Date(currentYear, patternStartMonth, patternStartDay);
    let fiscalEndDate = new Date(currentYear + 1, patternEndMonth, patternEndDay);
    
    // If we're before the fiscal year start, use previous year
    if (now < fiscalStartDate) {
        fiscalStartDate = new Date(currentYear - 1, patternStartMonth, patternStartDay);
        fiscalEndDate = new Date(currentYear, patternEndMonth, patternEndDay);
    }
    
    const currentStartYear = fiscalStartDate.getFullYear();
    const currentEndYear = fiscalEndDate.getFullYear();
    
    console.log('📅 Current fiscal year resolved:', {
        start: fiscalStartDate.toISOString().split('T')[0],
        end: fiscalEndDate.toISOString().split('T')[0],
        label: `FY ${currentStartYear} - ${currentEndYear}`
    });
    
    const options = [];
    
    // Generate options for current and previous 4 years
    for (let i = 0; i <= 4; i++) {
        const startYear = currentStartYear - i;
        const endYear = currentEndYear - i;
        
        options.push({
            value: i === 0 ? 'current' : `previous_${i}`,
            label: `FY ${startYear} - ${endYear}`,
            range: {
                start: `${startYear}-${String(patternStartMonth + 1).padStart(2, '0')}-${String(patternStartDay).padStart(2, '0')}`,
                end: `${endYear}-${String(patternEndMonth + 1).padStart(2, '0')}-${String(patternEndDay).padStart(2, '0')}`
            }
        });
    }
    
    return options;
};


const eventBelongsToFiscalYear = (event, eventType, fiscalYear) => {
    if (!fiscalYear?.start || !fiscalYear?.end) return true;

    const fyStart = parseLocalDate(fiscalYear.start);
    const fyEnd = parseLocalDate(fiscalYear.end);
    if (!fyStart || !fyEnd) return true;

    const dataValues = event?.dataValues || [];
    let startDate = '';
    let endDate = '';
    let pointDate = '';

    switch (eventType) {
        case 'goals':
            startDate = getDataValue(dataValues, DATA_ELEMENT_IDS.goalStart);
            endDate = getDataValue(dataValues, DATA_ELEMENT_IDS.goalEnd);
            pointDate = event.eventDate;
            break;
        case 'objectives':
            startDate = getDataValue(dataValues, DATA_ELEMENT_IDS.objectiveStart) || event.eventDate;
            endDate = getDataValue(dataValues, DATA_ELEMENT_IDS.objectiveEnd) || event.dueDate;
            pointDate = event.eventDate;
            break;
        case 'tasks':
            pointDate = getDataValue(dataValues, DATA_ELEMENT_IDS.taskWeek) || event.eventDate;
            break;
        case 'kras':
        case 'kpis':
        case 'leadership':
        default:
            startDate = event.eventDate;
            endDate = event.dueDate;
            pointDate = event.eventDate;
            break;
    }

    if (startDate && endDate) {
        const rangeStart = parseLocalDate(startDate);
        const rangeEnd = parseLocalDate(endDate);
        if (rangeStart && rangeEnd) {
            const normalizedFyEnd = new Date(fyEnd);
            normalizedFyEnd.setHours(23, 59, 59, 999);
            const normalizedRangeEnd = new Date(rangeEnd);
            normalizedRangeEnd.setHours(23, 59, 59, 999);
            return rangeStart <= normalizedFyEnd && normalizedRangeEnd >= fyStart;
        }
    }

    if (startDate && isWithinFiscalYear(startDate, fiscalYear)) return true;
    if (endDate && isWithinFiscalYear(endDate, fiscalYear)) return true;
    if (pointDate && isWithinFiscalYear(pointDate, fiscalYear)) return true;

    return false;
};


const DashboardPage = ({ userHasCompanyAccess = true }) => {

    const [currentFilter, setCurrentFilter] = useState('current'); // 'current', 'historical', 'all'

    // Helper: Check if a date falls within the current configured fiscal year
    const isDateInFiscalYear = useCallback((dateString) => {
        if (!dateString) return false;

        let currentFiscalYear = getFallbackFiscalYear();
        try {
            const storedCalendar = localStorage.getItem('company_financial_calendar');
            const parsedCalendar = storedCalendar ? JSON.parse(storedCalendar) : null;
            currentFiscalYear = buildFiscalYearOptions(parsedCalendar)[0]?.range || currentFiscalYear;
        } catch (error) {
            console.warn('Failed to resolve current fiscal year for date check:', error);
        }

        const date = new Date(dateString);
        const start = new Date(currentFiscalYear.start);
        const end = new Date(currentFiscalYear.end);
        end.setHours(23, 59, 59, 999);
        
        return date >= start && date <= end;
    }, []);

// Filter options for the view scope
const viewScopeFilters = [
    { value: 'current', label: 'Current Fiscal Year' },
    { value: 'historical', label: 'Historical Data' },
    { value: 'all', label: 'All Data' }
];

    // Filter options
    const availableFilters = [
        { value: 'current', label: 'Current Fiscal Year' },
        { value: 'historical', label: 'Historical Data' },
        { value: 'all', label: 'All Data' }
    ];

    // Get filtered data based on selection
    const getFilteredData = useCallback((data) => {
        if (!data?.length) return [];
        
        switch (currentFilter) {
            case 'current':
                return data.filter(item => {
                    const reviewDate = item.reviewDate || item.createdAt || item.eventDate || item.date;
                    return isDateInFiscalYear(reviewDate);
                });
                
            case 'historical':
                return data.filter(item => {
                    const reviewDate = item.reviewDate || item.createdAt || item.eventDate || item.date;
                    return !isDateInFiscalYear(reviewDate);
                });
                
            case 'all':
            default:
                return data;
        }
    }, [currentFilter, isDateInFiscalYear]);


    // ============================================================================
    // STEP 1: ALL HOOKS FIRST (Rules of Hooks) 
    // ============================================================================
    
    const engine = useDataEngine();

    // 1. Get current user
    const { loading: userLoading, error: userError, data: userData } = useDataQuery({
        me: {
            resource: 'me',
            params: { fields: 'id,displayName,organisationUnits[id,name,path,level],userCredentials[userRoles[name]]' }
        }
    });

    const FILTER_OPTIONS = {
        MY_PERFORMANCE: 'my_performance',
        MY_TEAM: 'my_team', 
        DEPARTMENT: 'department',
        ALL: 'all'
    };

    // 2. Extract basic values
    const currentUser = userData?.me;
    const userOrgUnits = userData?.me?.organisationUnits || [];
    const [selectedOrgUnitId, setSelectedOrgUnitId] = useState(userOrgUnits[0]?.id);
    const [selectedFilter, setSelectedFilter] = useState(FILTER_OPTIONS.ALL);
    const [fiscalYearOptions, setFiscalYearOptions] = useState([]);

    const [selectedFiscalYearKey, setSelectedFiscalYearKey] = useState('current');
    
    const [currentUserPosition, setCurrentUserPosition] = useState(null);
    const userOrgUnitId = selectedOrgUnitId || userOrgUnits[0]?.id;
    const userOrgUnitName = userOrgUnits.find(ou => ou.id === userOrgUnitId)?.name;
    const userName = userData?.me?.displayName;
    const selectedFiscalYear = React.useMemo(
        () => fiscalYearOptions.find(option => option.value === selectedFiscalYearKey)?.range || getFallbackFiscalYear(),
        [fiscalYearOptions, selectedFiscalYearKey]
    );
    const [dashboardData, setDashboardData] = useState([]);
    const [currentFilterExpanded, setCurrentFilterExpanded] = useState(false);
    
    
    
    // 3. ✅ CRITICAL: Fetch performance data BEFORE any code that uses it
    const { 
        loading: performanceLoading, 
        error: performanceError, 
        data: performanceData 
    } = usePerformanceHierarchy(userOrgUnitId);

    // 4. Other data fetches
    const { loading: orgLoading, error: orgError, data: orgData } = useOrgUnit(userOrgUnitId);
    const { loading: indicatorsLoading, error: indicatorsError, data: indicatorsData } = useIndicators();
    const { loading: dataElementsLoading, error: dataElementsError, data: dataElementsData } = useDataElements();

    // ============================================================================
    // STEP 2: FILTER OPTIONS & HELPER FUNCTIONS
    // ============================================================================
    
    const extractAttributes = (attributes = []) => {
        const map = {};
        attributes?.forEach(attr => { map[attr.attribute] = attr.value; });
        return map;
    };

    // Load fiscal year options from actual data
    useEffect(() => {
        if (!performanceData) return;
        
        try {
            // Collect all unique years from actual data
            const yearsWithData = new Set();
            
            // Extract years from goals
            performanceData.goals?.events?.forEach(event => {
                const date = event.eventDate || getDataValue(event.dataValues, DATA_ELEMENT_IDS.goalStart);
                if (date) {
                    const year = new Date(date).getFullYear();
                    if (year) yearsWithData.add(year);
                }
            });
            
            // Extract years from objectives
            performanceData.objectives?.events?.forEach(event => {
                const date = event.eventDate || getDataValue(event.dataValues, DATA_ELEMENT_IDS.objectiveStart);
                if (date) {
                    const year = new Date(date).getFullYear();
                    if (year) yearsWithData.add(year);
                }
            });
            
            // Extract years from tasks
            performanceData.tasks?.events?.forEach(event => {
                const date = event.eventDate || getDataValue(event.dataValues, DATA_ELEMENT_IDS.taskWeek);
                if (date) {
                    const year = new Date(date).getFullYear();
                    if (year) yearsWithData.add(year);
                }
            });
            
            // Also add current fiscal year based on configuration
            const storedCalendar = localStorage.getItem('company_financial_calendar');
            const parsedCalendar = storedCalendar ? JSON.parse(storedCalendar) : null;
            
            let patternStartMonth = 3; // April default
            let patternStartDay = 1;
            let patternEndMonth = 2; // March default
            let patternEndDay = 31;
            
            if (parsedCalendar?.financialYearStart && parsedCalendar?.financialYearEnd) {
                const startDate = new Date(parsedCalendar.financialYearStart);
                const endDate = new Date(parsedCalendar.financialYearEnd);
                patternStartMonth = startDate.getMonth();
                patternStartDay = startDate.getDate();
                patternEndMonth = endDate.getMonth();
                patternEndDay = endDate.getDate();
            }
            
            const now = new Date();
            const currentYear = now.getFullYear();
            let fiscalStartDate = new Date(currentYear, patternStartMonth, patternStartDay);
            
            if (now < fiscalStartDate) {
                fiscalStartDate = new Date(currentYear - 1, patternStartMonth, patternStartDay);
            }
            const currentFiscalStartYear = fiscalStartDate.getFullYear();
            
            // Add current fiscal year to the set
            yearsWithData.add(currentFiscalStartYear);
            
            // Sort years descending
            const sortedYears = Array.from(yearsWithData).sort((a, b) => b - a);
            
            // Build options only for years that have data
            const options = sortedYears.map((year, index) => {
                const isCurrent = year === currentFiscalStartYear;
                return {
                    value: isCurrent ? 'current' : `year_${year}`,
                    label: `FY ${year} - ${year + 1}`,
                    range: {
                        start: `${year}-${String(patternStartMonth + 1).padStart(2, '0')}-${String(patternStartDay).padStart(2, '0')}`,
                        end: `${year + 1}-${String(patternEndMonth + 1).padStart(2, '0')}-${String(patternEndDay).padStart(2, '0')}`
                    }
                };
            });
            
            setFiscalYearOptions(options);
            
            // Set selected to current if available, otherwise first option
            const hasCurrent = options.some(opt => opt.value === 'current');
            setSelectedFiscalYearKey(hasCurrent ? 'current' : (options[0]?.value || 'current'));
            
            console.log('✅ Fiscal year options loaded from data:', options);
        } catch (error) {
            console.warn('Failed to load fiscal year options from data:', error);
        }
    }, [performanceData]); 

    // Add this debug useEffect to see what's happening
    useEffect(() => {
        console.log('🔍 DEBUG - fiscalYearOptions:', fiscalYearOptions);
        console.log('🔍 DEBUG - selectedFiscalYearKey:', selectedFiscalYearKey);
        console.log('🔍 DEBUG - performanceData loaded:', !!performanceData);
    }, [fiscalYearOptions, selectedFiscalYearKey, performanceData]);

    // Add a fallback useEffect to ensure fiscalYearOptions are populated
    useEffect(() => {
        // If performanceData is not loaded yet or options are already set, skip
        if (fiscalYearOptions.length > 0) return;
        
        // Try to load from localStorage as fallback
        try {
            const storedCalendar = localStorage.getItem('company_financial_calendar');
            const parsedCalendar = storedCalendar ? JSON.parse(storedCalendar) : null;
            
            if (parsedCalendar?.financialYearStart && parsedCalendar?.financialYearEnd) {
                const startDate = new Date(parsedCalendar.financialYearStart);
                const endDate = new Date(parsedCalendar.financialYearEnd);
                const startYear = startDate.getFullYear();
                const endYear = endDate.getFullYear();
                
                // Create fallback options
                const options = [
                    { value: 'current', label: `FY ${startYear} - ${endYear}`, range: { start: parsedCalendar.financialYearStart, end: parsedCalendar.financialYearEnd } }
                ];
                
                // Add previous year
                const prevStartYear = startYear - 1;
                const prevEndYear = endYear - 1;
                options.push({
                    value: 'previous_1',
                    label: `FY ${prevStartYear} - ${prevEndYear}`,
                    range: {
                        start: `${prevStartYear}-${String(startDate.getMonth() + 1).padStart(2, '0')}-${String(startDate.getDate()).padStart(2, '0')}`,
                        end: `${prevEndYear}-${String(endDate.getMonth() + 1).padStart(2, '0')}-${String(endDate.getDate()).padStart(2, '0')}`
                    }
                });
                
                setFiscalYearOptions(options);
                setSelectedFiscalYearKey('current');
                console.log('📅 Fallback fiscal year options loaded:', options);
            }
        } catch (error) {
            console.warn('Failed to load fallback fiscal year options:', error);
        }
    }, [fiscalYearOptions.length, performanceData]);

    // Add a useEffect to ensure selectedFiscalYearKey is always valid
    useEffect(() => {
        if (fiscalYearOptions.length === 0) return;
        
        // Check if current selected value exists in options
        const isValid = fiscalYearOptions.some(opt => opt.value === selectedFiscalYearKey);
        
        if (!isValid) {
            // Find the best option to select
            const currentOption = fiscalYearOptions.find(opt => opt.value === 'current');
            const firstOption = fiscalYearOptions[0];
            const newValue = currentOption?.value || firstOption?.value;
            
            if (newValue) {
                console.log('📅 Fixing invalid selectedFiscalYearKey:', selectedFiscalYearKey, '->', newValue);
                setSelectedFiscalYearKey(newValue);
            }
        }
    }, [fiscalYearOptions, selectedFiscalYearKey]);

    const getUserRoleLevel = (userData, performanceData, currentUserPosition) => {
        const roles = userData?.me?.userCredentials?.userRoles || [];
        const roleNames = roles.map(r => r.name?.toLowerCase() || '');
        if (roleNames.some(r => r.includes('ceo') || r.includes('executive') || r.includes('director'))) return 'executive';
        if (roleNames.some(r => r.includes('hod') || r.includes('head') || r.includes('manager'))) return 'manager';
        if (currentUserPosition?.trackedEntityInstance) {
            const directReports = getDirectReportPositions(performanceData, currentUserPosition);
            if (directReports.length > 0) return 'manager';
        }
        return 'staff';
    };
    const userRoleLevel = getUserRoleLevel(userData, performanceData, currentUserPosition);

    const getAccessibleOrgUnits = (userOrgUnits, filter, userRoleLevel, performanceData, currentUserPosition) => {
        const currentOrgUnitId = userOrgUnits[0]?.id;
        const positions = performanceData?.positions?.trackedEntityInstances || [];
        const staff = performanceData?.staff?.trackedEntityInstances || [];
        
        switch (filter) {
            case FILTER_OPTIONS.MY_PERFORMANCE:
                return {
                    orgUnits: [currentOrgUnitId].filter(Boolean),
                    positionTeis: [currentUserPosition?.trackedEntityInstance].filter(Boolean),
                    scope: 'self'
                };
            case FILTER_OPTIONS.MY_TEAM:
                const directReportPositions = getDirectReportPositions(performanceData, currentUserPosition);
                const directReportOrgUnits = directReportPositions.map(pos => {
                    const attrs = extractAttributes(pos.attributes || []);
                    const staffId = attrs['V66IFsw5kq6'];
                    const staffMember = staff.find(s => s.trackedEntityInstance === staffId);
                    return staffMember?.orgUnit;
                }).filter(Boolean);
                return {
                    orgUnits: [...new Set(directReportOrgUnits)],
                    positionTeis: directReportPositions.map(p => p.trackedEntityInstance).filter(Boolean),
                    scope: 'team'
                };
            case FILTER_OPTIONS.DEPARTMENT:
                const departmentOrgUnits = getDepartmentOrgUnits(performanceData, currentOrgUnitId);
                const departmentPositions = positions.filter(pos => departmentOrgUnits.includes(pos.orgUnit)) || [];
                return {
                    orgUnits: departmentOrgUnits,
                    positionTeis: departmentPositions.map(p => p.trackedEntityInstance),
                    scope: 'department'
                };
            case FILTER_OPTIONS.ALL:
            default:
                return {
                    orgUnits: userOrgUnits.map(ou => ou.id),
                    positionTeis: positions.map(p => p.trackedEntityInstance),
                    scope: 'all'
                };
        }
    };

    const getDirectReportPositions = (performanceData, currentUserPosition) => {
        if (!currentUserPosition?.trackedEntityInstance || !performanceData?.positions?.trackedEntityInstances) {
            return [];
        }
        const currentPositionId = currentUserPosition.trackedEntityInstance;
        const allPositions = performanceData.positions.trackedEntityInstances;
        const directReports = allPositions.filter(pos => {
            const attrs = extractAttributes(pos.attributes || []);
            return attrs['SjP47qi3JeB'] === currentPositionId;
        });
        return directReports;
    };

    const getDepartmentOrgUnits = (performanceData, rootOrgUnitId) => {
        if (!performanceData?.orgUnits?.organisationUnits) {
            return [rootOrgUnitId].filter(Boolean);
        }
        const allOrgUnits = performanceData.orgUnits.organisationUnits;
        const rootOrgUnit = allOrgUnits.find(ou => ou.id === rootOrgUnitId);
        if (!rootOrgUnit?.path) return [rootOrgUnitId];
        const rootPath = rootOrgUnit.path;
        return allOrgUnits
            .filter(ou => ou.path?.startsWith(rootPath))
            .map(ou => ou.id);
    };

    // ============================================================================
    // STEP 3: useMemo for filtered data (applies filter to ALL content)
    // ============================================================================
    
    const accessibleData = React.useMemo(() => {
        return getAccessibleOrgUnits(
            userOrgUnits, 
            selectedFilter, 
            userRoleLevel, 
            performanceData,
            currentUserPosition
        );
    }, [userOrgUnits, selectedFilter, userRoleLevel, performanceData, currentUserPosition]);

    const accessiblePerformanceData = React.useMemo(() => {
        if (!performanceData) return null;

        const orgUnitSet = new Set(accessibleData.orgUnits || []);
        const positionTeiSet = new Set(accessibleData.positionTeis || []);
        const taskPositionElementId = 'SlgO60cMK3F';

        const filterEventsByOrgUnits = (collection) => ({
            ...collection,
            events: (collection?.events || []).filter(event =>
                accessibleData.scope === 'all' || orgUnitSet.has(event.orgUnit)
            )
        });

        const filterObjectivesByPosition = (collection) => ({
            ...collection,
            events: (collection?.events || []).filter(event =>
                accessibleData.scope === 'all' ||
                positionTeiSet.size === 0 ||
                positionTeiSet.has(event.trackedEntityInstance)
            )
        });

        const filterTasks = (collection) => ({
            ...collection,
            events: (collection?.events || []).filter(event => {
                if (accessibleData.scope === 'all') return true;
                const taskPositionTei = event.dataValues?.find(
                    dv => dv.dataElement === taskPositionElementId
                )?.value;
                return orgUnitSet.has(event.orgUnit) ||
                    (taskPositionTei && positionTeiSet.has(taskPositionTei));
            })
        });

        return {
            ...performanceData,
            goals: performanceData.goals,
            objectives: filterObjectivesByPosition(performanceData.objectives),
            tasks: filterTasks(performanceData.tasks),
            kras: filterEventsByOrgUnits(performanceData.kras),
            kpis: filterEventsByOrgUnits(performanceData.kpis),
            positionHistory: filterEventsByOrgUnits(performanceData.positionHistory),
        };
    }, [performanceData, accessibleData]);

    
    // Add this after your fiscalYearOptions useEffect
    const currentFiscalYear = useMemo(() => {
        // First try to get from fiscalYearOptions (which uses stored calendar)
        const currentOption = fiscalYearOptions.find(option => option.value === 'current');
        if (currentOption?.range) {
            return currentOption.range;
        }
        
        // If no options, try to build directly from stored calendar
        try {
            const storedCalendar = localStorage.getItem('company_financial_calendar');
            const parsedCalendar = storedCalendar ? JSON.parse(storedCalendar) : null;
            
            if (parsedCalendar?.financialYearStart && parsedCalendar?.financialYearEnd) {
                const startDate = new Date(parsedCalendar.financialYearStart);
                const endDate = new Date(parsedCalendar.financialYearEnd);
                const patternStartMonth = startDate.getMonth();
                const patternStartDay = startDate.getDate();
                const patternEndMonth = endDate.getMonth();
                const patternEndDay = endDate.getDate();
                
                // Calculate current fiscal year based on today's date
                const now = new Date();
                const currentYear = now.getFullYear();
                
                let fiscalStartDate = new Date(currentYear, patternStartMonth, patternStartDay);
                let fiscalEndDate = new Date(currentYear + 1, patternEndMonth, patternEndDay);
                
                if (now < fiscalStartDate) {
                    fiscalStartDate = new Date(currentYear - 1, patternStartMonth, patternStartDay);
                    fiscalEndDate = new Date(currentYear, patternEndMonth, patternEndDay);
                }
                
                return {
                    start: `${fiscalStartDate.getFullYear()}-${String(patternStartMonth + 1).padStart(2, '0')}-${String(patternStartDay).padStart(2, '0')}`,
                    end: `${fiscalEndDate.getFullYear()}-${String(patternEndMonth + 1).padStart(2, '0')}-${String(patternEndDay).padStart(2, '0')}`
                };
            }
        } catch (error) {
            console.warn('Failed to get current fiscal year from stored calendar:', error);
        }
        
        // Ultimate fallback - use April-March pattern (since that's your actual configuration)
        const now = new Date();
        const currentYear = now.getFullYear();
        const aprilFirst = new Date(currentYear, 3, 1); // April 1
        
        if (now >= aprilFirst) {
            return { 
                start: `${currentYear}-04-01`, 
                end: `${currentYear + 1}-03-31` 
            };
        } else {
            return { 
                start: `${currentYear - 1}-04-01`, 
                end: `${currentYear}-03-31` 
            };
        }
    }, [fiscalYearOptions]);

    const effectiveFiscalYear = useMemo(() => {
        // If we're in 'all' mode, return null (no filtering)
        if (currentFilter === 'all') {
            return null;
        }
        
        // If we're in 'historical' mode AND a specific year is selected (not 'current')
        if (currentFilter === 'historical' && selectedFiscalYearKey !== 'current') {
            return selectedFiscalYear;
        }
        
        // If we're in 'current' mode AND a specific year is selected (not 'current')
        // This allows the dropdown to override the current mode
        if (currentFilter === 'current' && selectedFiscalYearKey !== 'current') {
            return selectedFiscalYear;
        }
        
        // Default: use current fiscal year
        return currentFiscalYear;
    }, [currentFilter, selectedFiscalYearKey, selectedFiscalYear, currentFiscalYear]);

    const filteredByFiscalYear = useMemo(() => {
    if (!accessiblePerformanceData) return null;
    
    const applyFilter = (collection, eventType) => {
        if (!collection?.events) return collection;

        const events = collection.events.filter(event => {
            // If 'all' is selected, show everything
            if (currentFilter === 'all') return true;
            
            // If a specific year is selected from dropdown (not 'current')
            if (selectedFiscalYearKey !== 'current') {
                return eventBelongsToFiscalYear(event, eventType, selectedFiscalYear);
            }
            
            // Otherwise, use current filter logic
            if (currentFilter === 'current') {
                return eventBelongsToFiscalYear(event, eventType, currentFiscalYear);
            }
            
            if (currentFilter === 'historical') {
                return !eventBelongsToFiscalYear(event, eventType, currentFiscalYear);
            }
            
            return true;
        });

        return { ...collection, events };
    };
    
    return {
        ...accessiblePerformanceData,
        goals: applyFilter(accessiblePerformanceData.goals, 'goals'),
        objectives: applyFilter(accessiblePerformanceData.objectives, 'objectives'),
        tasks: applyFilter(accessiblePerformanceData.tasks, 'tasks'),
        kras: applyFilter(accessiblePerformanceData.kras, 'kras'),
        kpis: applyFilter(accessiblePerformanceData.kpis, 'kpis'),
    };
}, [accessiblePerformanceData, currentFilter, currentFiscalYear, selectedFiscalYearKey, selectedFiscalYear]);

    // ============================================================================
    // STEP 4: useEffect for debugging & position loading
    // ============================================================================
    
    React.useEffect(() => {
        console.log('🔍 [FILTER DEBUG]', {
            selectedFilter,
            userRoleLevel,
            accessibleData,
            originalOrgUnits: userOrgUnits.map(ou => ou.id)
        });
    }, [selectedFilter, userRoleLevel, accessibleData, userOrgUnits]);

    useEffect(() => {
        const loadPosition = async () => {
            if (!currentUser?.id || !engine) return;
            try {
                const response = await engine.query({
                    currentUserPosition: {
                        resource: 'trackedEntityInstances',
                        params: {
                            trackedEntityType: 'UGGo0Ebjl0E',
                            filter: `V66IFsw5kq6:eq:${currentUser.id}`,
                            fields: 'trackedEntityInstance,attributes[attribute,value]',
                            ouMode: 'ACCESSIBLE',
                            pageSize: 1
                        }
                    }
                });
                const position = response.currentUserPosition?.trackedEntityInstances?.[0];
                if (position) {
                    setCurrentUserPosition(position);
                    console.log('✅ [POSITION] Loaded user position:', position.trackedEntityInstance);
                }
            } catch (error) {
                console.error('❌ [POSITION] Failed to load position:', error);
            }
        };
        loadPosition();
    }, [currentUser?.id, engine]);

    // ============================================================================
    // STEP 5: Loading/Error Early Returns
    // ============================================================================
    
    if (userLoading) {
        return (
            <div style={{ padding: 24 }}>
                <CircularLoader small />
                <BodyText style={{ textAlign: 'center', marginTop: 16 }}>
                    Loading your dashboard...
                </BodyText>
            </div>
        );
    }

    if (userError || !userOrgUnitId) {
        return (
            <div style={{ padding: 24 }}>
                <AlertBar critical>
                    {userError?.message || 'Unable to determine your organization unit. Please contact administrator.'}
                </AlertBar>
            </div>
        );
    }

    if (orgLoading || indicatorsLoading || performanceLoading) {
        return (
            <div style={{ padding: 24 }}>
                <CircularLoader small />
                <BodyText style={{ textAlign: 'center', marginTop: 16 }}>
                    Loading dashboard data for {userOrgUnitName}...
                </BodyText>
            </div>
        );
    }

    if (orgError || indicatorsError || performanceError) {
        return (
            <div style={{ padding: 24 }}>
                <AlertBar critical>
                    Error loading dashboard data: {orgError?.message || indicatorsError?.message || performanceError?.message}
                </AlertBar>
            </div>
        );
    }

    // ============================================================================
    // STEP 6: FilterSelector Component (defined inside, after hooks)
    // ============================================================================
    
    const FilterSelector = ({ currentFilter, onFilterChange, userRoleLevel, orgUnitName }) => {
        const availableFilters = React.useMemo(() => {
            const filters = [
                { 
                    value: FILTER_OPTIONS.MY_PERFORMANCE, 
                    label: '👤 My Performance', 
                    roles: ['staff', 'manager', 'executive'],
                    description: 'View only your own objectives and tasks'
                },
                { 
                    value: FILTER_OPTIONS.MY_TEAM, 
                    label: '👥 Team Performance', 
                    roles: ['manager', 'executive'],
                    description: 'View your direct reports\' performance'
                },
                { 
                    value: FILTER_OPTIONS.DEPARTMENT, 
                    label: `🏢 ${orgUnitName || 'Department'} Department`, 
                    roles: ['manager', 'executive'],
                    description: 'View entire department performance'
                },
                { 
                    value: FILTER_OPTIONS.ALL, 
                    label: '🌐 Overall Company Performance', 
                    roles: ['staff', 'manager', 'executive'],
                    description: 'Clear the current scope filter'
                }
            ];
            return filters.filter(f => f.roles.includes(userRoleLevel));
        }, [userRoleLevel, orgUnitName]);

        if (availableFilters.length === 0) return null;

        return (
            <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                flexWrap: 'wrap'
            }}>
                {availableFilters.map(filter => (
                    <button
                        key={filter.value}
                        onClick={() => onFilterChange(filter.value)}
                        style={{
                            padding: '6px 14px',
                            borderRadius: '999px',
                            fontSize: '0.8rem',
                            fontWeight: currentFilter === filter.value ? 600 : 400,
                            backgroundColor: currentFilter === filter.value
                                ? 'rgba(255,255,255,0.22)'
                                : 'rgba(255,255,255,0.07)',
                            color: currentFilter === filter.value ? '#ffffff' : 'rgba(255,255,255,0.65)',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            border: `1px solid ${currentFilter === filter.value ? 'rgba(255,255,255,0.55)' : 'rgba(255,255,255,0.18)'}`,
                            boxShadow: currentFilter === filter.value ? '0 2px 8px rgba(0,0,0,0.18)' : 'none',
                        }}
                        onMouseEnter={(e) => {
                            if (currentFilter !== filter.value) {
                                e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.14)';
                                e.currentTarget.style.color = '#ffffff';
                            }
                        }}
                        onMouseLeave={(e) => {
                            if (currentFilter !== filter.value) {
                                e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.07)';
                                e.currentTarget.style.color = 'rgba(255,255,255,0.65)';
                            }
                        }}
                    >
                        {filter.label}
                    </button>
                ))}
            </div>
        );
    };

    // ============================================================================
    // STEP 7: JSX Return - Filter at TOP, applied to ALL sections
    // ============================================================================
    
    const effectivePerformanceData = filteredByFiscalYear || accessiblePerformanceData;

    return (
        <div style={{ 
            padding: '10px', 
            marginTop: '1px', 
            fontFamily: '"Segoe UI", Tahoma, Geneva, Verdana, sans-serif',
            minHeight: '100vh'
        }}>
            {/* ===== HEADER SECTION ===== */}
            <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center', 
                marginBottom: 8,
                flexWrap: 'wrap',
                gap: '12px'
            }}>
                <h3 style={{ color: '#054382ff', margin: 0 }}>Dashboard</h3>
                <div style={{ textAlign: 'right' }}>
                    <h3 style={{ color: '#054382ff', margin: 0, fontSize: '1.1rem' }}>{userOrgUnitName}</h3>
                    <div style={{ fontSize: '0.9rem', color: '#666' }}>Welcome, {userName}</div>
                </div>
            </div>

            <hr style={{ width: '100%', border: '1.5px solid #c0c3c5ff', margin: '8px 0 16px 0' }} />
            
            {/* ===== STICKY FILTER SECTION WITH HOVER EXPAND ===== */}
            <div
                style={{
                    position: 'sticky',
                    top: '10px',
                    zIndex: 100,
                    marginBottom: '20px',
                    borderRadius: '16px',
                    boxShadow: '0 8px 30px rgba(5, 67, 130, 0.35), 0 2px 8px rgba(0, 0, 0, 0.15)',
                    border: '2px solid rgba(255, 255, 255, 0.3)',
                }}
            >
                <div style={{
                    background: 'linear-gradient(135deg, #208205 0%, #a2c205 100%)',
                    padding: '14px 20px',
                    borderRadius: '16px',
                }}
                onMouseEnter={() => setCurrentFilterExpanded(true)}
                onMouseLeave={() => setCurrentFilterExpanded(false)}
                >
                {/* Collapsed View - Always visible */}
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    flexWrap: 'wrap',
                    gap: 12,
                }}>
               {/* REPLACED "View:" buttons with fiscal year dropdown + All Data button */}
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                <span style={{
                    fontWeight: 600,
                    color: '#ffffff',
                    fontSize: '0.7rem',
                    whiteSpace: 'nowrap',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    justifyContent: 'center',
                    height: 14,
                    display: 'flex',
                    gap: 6 
                }}>
                    <Calendar1Icon size={14} /> Period:
                </span>

                {/* Fiscal year dropdown */}
                <select
                    key={fiscalYearOptions.length}
                    value={selectedFiscalYearKey}
                    onChange={(e) => {
                        console.log('📅 Fiscal year selected:', e.target.value);
                        setSelectedFiscalYearKey(e.target.value);
                    }}
                    style={{
                        padding: '4px 22px 4px 10px',
                        borderRadius: '16px',
                        fontSize: '0.75rem',
                        width: 'auto',
                        minWidth: '120px',
                        backgroundColor: '#ffffff',
                        border: '1px solid rgba(255,255,255,0.3)',
                        cursor: 'pointer',
                        color: '#2c3e50',
                        fontWeight: 500,
                        appearance: 'none',
                        backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%23666' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`,
                        backgroundRepeat: 'no-repeat',
                        backgroundPosition: 'right 8px center',
                    }}
                >
                    {fiscalYearOptions.map(option => (
                        <option key={option.value} value={option.value}>
                            {option.value === 'current' 
                                ? `${option.label} - Current` 
                                : option.label}
                        </option>
                    ))}
                </select>

                {/* "All Data" button next to dropdown */}
                <button
                    onClick={() => setCurrentFilter('all')}
                    style={{
                        padding: '4px 12px',
                        borderRadius: '20px',
                        fontSize: '0.75rem',
                        fontWeight: currentFilter === 'all' ? 600 : 400,
                        backgroundColor: currentFilter === 'all' ? '#ffffff' : 'rgba(255,255,255,0.15)',
                        color: currentFilter === 'all' ? '#208205' : '#ffffff',
                        cursor: 'pointer',
                        border: 'none',
                        transition: 'all 0.2s ease',
                    }}
                >
                    All Data
                </button>
            </div>

            {/* Calendar display */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 15, flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <div style={{ display: 'flex', justifyContent: 'center', width: 14, height: 14, color: 'rgba(255,255,255,255)' }}>
                        <Calendar1Icon size={16} />
                    </div>
                    <span style={{ fontSize: '0.7rem', color: '#ffffff', fontWeight: 500 }}>
                        {currentFiscalYear ? `${new Date(currentFiscalYear.start).getFullYear()} - ${new Date(currentFiscalYear.end).getFullYear()}` : 'Loading...'}
                    </span>
                </div>
                <div style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,255)', display: 'flex', justifyContent: 'center', gap: 6, height: 14 }}>
                    <span><ExpandIcon size={14} /></span>
                    <span style={{ lineHeight: '14px' }}>Hover to expand</span>
                </div>
            </div>
        </div>

        {/* Expanded View - Shows on hover */}
        {currentFilterExpanded && (
            <div style={{
                marginTop: '16px',
                paddingTop: '16px',
                borderTop: '1px solid rgba(255,255,255,0.2)',
                display: 'flex',
                flexDirection: 'column',
                gap: 16,
            }}>
                {/* Performance Scope (Org Unit Filter) */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                    <span style={{
                        fontWeight: 600,
                        color: '#ffffff',
                        fontSize: '0.7rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        minWidth: '60px',
                    }}>
                        Performance Scope:
                    </span>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        {[
                            { value: FILTER_OPTIONS.MY_PERFORMANCE, label: '👤 My Performance' },
                            { value: FILTER_OPTIONS.MY_TEAM, label: '👥 My Team' },
                            { value: FILTER_OPTIONS.DEPARTMENT, label: `🏢 ${userOrgUnitName?.split(' ')[0] || 'Department'}` },
                            { value: FILTER_OPTIONS.ALL, label: '🌐 All' },
                        ].filter(filter => {
                            if (filter.value === FILTER_OPTIONS.MY_TEAM && userRoleLevel !== 'manager' && userRoleLevel !== 'executive') return false;
                            if (filter.value === FILTER_OPTIONS.DEPARTMENT && userRoleLevel !== 'manager' && userRoleLevel !== 'executive') return false;
                            return true;
                        }).map(filter => (
                            <button
                                key={filter.value}
                                onClick={() => setSelectedFilter(filter.value)}
                                style={{
                                    padding: '5px 14px',
                                    borderRadius: '20px',
                                    fontSize: '0.75rem',
                                    fontWeight: selectedFilter === filter.value ? 600 : 400,
                                    backgroundColor: selectedFilter === filter.value ? '#ffffff' : 'rgba(255,255,255,0.15)',
                                    color: selectedFilter === filter.value ? '#208205' : '#ffffff',
                                    cursor: 'pointer',
                                    border: 'none',
                                    transition: 'all 0.2s ease',
                                }}
                            >
                                {filter.label}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Active Filters Badge */}
                {(selectedFilter !== FILTER_OPTIONS.ALL || selectedFiscalYearKey !== 'current' || currentFilter !== 'current') && (
                    <div style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 8,
                        flexWrap: 'wrap',
                        padding: '6px 12px',
                        backgroundColor: 'rgba(0, 0, 0, 0.25)',
                        borderRadius: '12px',
                        fontSize: '0.7rem',
                        color: 'rgba(255,255,255,0.9)',
                        alignSelf: 'flex-start',
                    }}>
                        <span>🎯 Active:</span>
                        <span>{viewScopeFilters.find(f => f.value === currentFilter)?.label || 'Current'}</span>
                        {selectedFilter === FILTER_OPTIONS.MY_PERFORMANCE && <span>· My Performance</span>}
                        {selectedFilter === FILTER_OPTIONS.MY_TEAM && <span>· My Team</span>}
                        {selectedFilter === FILTER_OPTIONS.DEPARTMENT && <span>· {userOrgUnitName?.split(' ')[0]} Dept</span>}
                        {selectedFiscalYearKey !== 'current' && (
                            <span>· {fiscalYearOptions.find(o => o.value === selectedFiscalYearKey)?.label?.replace(' (Current)', '')}</span>
                        )}
                        <button
                            onClick={() => {
                                setCurrentFilter('current');
                                setSelectedFilter(FILTER_OPTIONS.ALL);
                                setSelectedFiscalYearKey('current');
                            }}
                            style={{
                                background: 'rgba(255,255,255,0.2)',
                                border: 'none',
                                color: '#ffffff',
                                cursor: 'pointer',
                                fontSize: '0.7rem',
                                borderRadius: '12px',
                                padding: '2px 8px',
                            }}
                        >
                            ✕ Clear
                        </button>
                    </div>
                )}
            </div>
        )}
    </div>
</div>

{/* 🔥 MOVED ACTIVE FILTERS BADGE OUTSIDE CARD (STICKY) */}
{(selectedFilter !== FILTER_OPTIONS.ALL || selectedFiscalYearKey !== 'current' || currentFilter !== 'current') && (
    <div style={{
        position: 'sticky',
        top: '80px', // Below the main filter card
        zIndex: 99,
        marginBottom: '20px',
        padding: '8px 16px',
        backgroundColor: 'rgba(0, 0, 0, 0.25)',
        borderRadius: '12px',
        fontSize: '0.75rem',
        color: 'rgba(255,255,255,0.9)',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 8,
        flexWrap: 'wrap',
    }}>
        <span>🎯 Active Filters:</span>
        {currentFilter !== 'current' && <span>· {viewScopeFilters.find(f => f.value === currentFilter)?.label}</span>}
        {selectedFilter === FILTER_OPTIONS.MY_PERFORMANCE && <span>· My Performance</span>}
                    {selectedFilter === FILTER_OPTIONS.MY_TEAM && <span>· My Team</span>}
                    {selectedFilter === FILTER_OPTIONS.DEPARTMENT && <span>· {userOrgUnitName?.split(' ')[0]} Dept</span>}
                    {selectedFiscalYearKey !== 'current' && (
                        <span>· {fiscalYearOptions.find(o => o.value === selectedFiscalYearKey)?.label?.replace(' (Current)', '')}</span>
                    )}
                    <button
                        onClick={() => {
                            setCurrentFilter('current');
                            setSelectedFilter(FILTER_OPTIONS.ALL);
                            setSelectedFiscalYearKey('current');
                        }}
                        style={{
                            background: 'rgba(255,255,255,0.3)',
                            border: 'none',
                            color: '#ffffff',
                            cursor: 'pointer',
                            fontSize: '0.7rem',
                            borderRadius: '12px',
                            padding: '2px 8px',
                        }}
                    >
                        ✕ Clear
                    </button>
                </div>
            )}

            {/* ===== ALL SECTIONS NOW RECEIVE FILTERED DATA ===== */}
            
            {/* Organisation Unit Info */}
            <ReviewReminders
                orgUnitId={userOrgUnitId}
                performanceData={effectivePerformanceData}
                currentFilter={currentFilter}
                selectedFiscalYear={selectedFiscalYear}
            />

            {/* Goals Section */}
            <div style={{ 
                display: 'flex', 
                color: 'green', 
                justifyContent: 'space-between', 
                alignItems: 'center', 
                marginBottom: 4, 
                marginTop: '24px' 
            }}> 
                <h3 style={{ margin: 0 }}>Goals Section</h3>
            </div>
            <hr style={{ width: '100%', border: '1px solid #e0e0e0', margin: '10px 0 24px 0' }} />
            <RealGoals 
                orgUnitId={userOrgUnitId} 
                performanceData={effectivePerformanceData}
                accessibleOrgUnitIds={accessibleData.orgUnits}
                positionTeis={accessibleData.positionTeis}  
                filterScope={accessibleData.scope}          
            />

            {/* Objectives Section */}
            <div style={{ 
                display: 'flex', 
                color: 'green', 
                justifyContent: 'space-between', 
                alignItems: 'center', 
                marginBottom: 6, 
                marginTop: '24px' 
            }}> 
                <h3 style={{ margin: 0 }}>Objectives Section</h3>
            </div>
            <hr style={{ width: '100%', border: '1px solid #e0e0e0', margin: '10px 0 24px 0' }} />
            <Objectives 
                orgUnitId={userOrgUnitId}
                currentUser={{ name: userName, id: userData?.me?.id }}
                performanceData={effectivePerformanceData}
                accessibleOrgUnitIds={accessibleData.orgUnits}
                positionTeis={accessibleData.positionTeis}  
                filterScope={accessibleData.scope}          
            />

            {/* KPI Section */}
            <div style={{ 
                display: 'flex', 
                color: 'green', 
                justifyContent: 'space-between', 
                alignItems: 'center', 
                marginBottom: 6, 
                marginTop: '24px' 
            }}> 
                <h3 style={{ margin: 0 }}>Key Performance Indicators Section</h3>
            </div>
            <hr style={{ width: '100%', border: '1px solid #e0e0e0', margin: '10px 0 24px 0' }} />
            <KPIKRAReports
                orgUnitId={userOrgUnitId}
                performanceData={effectivePerformanceData}
                accessibleOrgUnitIds={accessibleData.orgUnits} 
                filterScope={accessibleData.scope}            
            /> 
            
            {/* Tasks Section */}
            <div style={{ 
                display: 'flex', 
                color: 'green', 
                justifyContent: 'space-between', 
                alignItems: 'center', 
                marginBottom: 16,
                marginTop: '24px'
            }}> 
                <h3 style={{ margin: 0 }}>Tasks Section</h3>
            </div>
            <hr style={{ width: '100%', border: '1px solid #e0e0e0', margin: '10px 0 24px 0' }} /> 
            <div style={{ marginTop: 24 }}>
                <RealTasks 
                    orgUnitId={userOrgUnitId}
                    performanceData={effectivePerformanceData}
                    accessibleOrgUnitIds={accessibleData.orgUnits}
                    positionTeis={accessibleData.positionTeis}
                    filterScope={accessibleData.scope}
                />
            </div>
            <TaskMetrics
                orgUnitId={userOrgUnitId}
                performanceData={effectivePerformanceData}
                accessibleOrgUnitIds={accessibleData.orgUnits} 
                filterScope={accessibleData.scope}
            />

            {/* Leadership Section */}
            <div style={{ 
                display: 'flex', 
                color: 'green', 
                justifyContent: 'space-between', 
                alignItems: 'center', 
                marginBottom: 16,
                marginTop: '24px'
            }}> 
                <h3 style={{ margin: 0 }}>Leadership and Behaviors Section</h3>
            </div>
            <hr style={{ width: '100%', border: '1px solid #e0e0e0', margin: '10px 0 24px 0' }} />
            <div style={{ display: 'grid', marginTop: '10px', gridTemplateColumns: '1fr 1fr', gap: 24, marginBottom: 24 }}>
                <LeadershipDashboard
                    orgUnitId={userOrgUnitId}
                    accessibleOrgUnitIds={accessibleData.orgUnits} 
                    filterScope={accessibleData.scope} 
                />
            </div>
        </div>
    );
};

export default DashboardPage;

